import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View, Text, ScrollView, Pressable, Platform, Linking } from 'react-native'
import { useLocalSearchParams, usePathname, useRouter, type Href } from 'expo-router'
import { ArrowLeft } from 'lucide-react-native'
import { useTheme } from '../../../theme/useTheme'
import { useThemeContext } from '../../../theme/ThemeContext'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { useDialog } from '../../../shared/hooks/useDialog'
import { useApiClient } from '../../../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../../../constants/api'
import { useSupabaseAuth } from '../../../shared/contexts/SupabaseAuthContext'
import { useAccountAdmin } from '../../../accounts/hooks/useAccountAdmin'
import { useConsumerBillingStripeSync } from '../../../accounts/hooks/useConsumerBillingStripeSync'
import { useB2bBillingActions } from '../../../accounts/hooks/useB2bBillingActions'
import { useConsumerProBillingActions } from '../../../accounts/hooks/useConsumerProBillingActions'
import {
  ACCOUNT_ADMIN_NAV,
  ACCOUNT_ADMIN_PUBLIC_ROUTE,
} from '../../../accounts/constants/accountAdmin.constants'
import { buildAccountAdminNavPath } from '../../../accounts/helpers/accountAdminNav.helper'
import { usePricingI18n } from '../../hooks/usePricingI18n'
import type { ConsumerProBillingInterval } from '../../constants/consumerProBilling.constants'
import { ENTERPRISE_CONTACT_PATH } from '../../constants/enterpriseContact.constants'
import { buildPublicSignInHref } from '../../../signIn/helpers/postAuthRedirect.helper'
import { useStableConsumerPlanForPricing } from '../../hooks/useStableConsumerPlanForPricing'
import { getStoreSubscriptionsUrl, isStripeManagedProvider } from '../../../shared/helpers/proSubscriptionManage.helper'
import {
  isPricingProductionMode,
  resolvePricingPlanForEnv,
} from '../../helpers/pricingProductionMode.helper'
import { getPricingScreenStyles } from './PricingScreen.styles'
import { PricingScreenPlanCard } from './PricingScreenPlanCard'
import { MarketingPageFooter } from '../../../../components/landing/MarketingPageFooter/MarketingPageFooter'
import { isOrgB2bStripeSubscriptionStatusPaid } from '../../helpers/pricingOrgSubscription.helper'

export function PricingScreen() {
  const theme = useTheme()
  const { themeMode } = useThemeContext()
  const router = useRouter()
  const pathname = usePathname()
  const styles = useMemo(() => getPricingScreenStyles(theme, themeMode), [theme, themeMode])
  const isProductionMode = isPricingProductionMode()
  const apiClient = useApiClient()
  const { isLoaded, isSignedIn } = useSupabaseAuth()
  const { user, loading: accountAdminLoading, refresh: refreshAccountAdmin } = useAccountAdmin()

  const {
    consumerPlanTier,
    consumerProBillingInterval,
    consumerCancelAtPeriodEnd,
    consumerPeriodEndIso,
  } = useStableConsumerPlanForPricing(user, accountAdminLoading)

  useConsumerBillingStripeSync(refreshAccountAdmin, {
    enabled: Boolean(isLoaded && isSignedIn && user),
  })

  const params = useLocalSearchParams<{ billing?: string | string[]; session_id?: string | string[] }>()
  const billingQueryFlag = useMemo(() => {
    const b = params.billing
    const raw = typeof b === 'string' ? b : Array.isArray(b) ? b[0] : undefined
    return typeof raw === 'string' ? raw.trim().toLowerCase() : ''
  }, [params.billing])
  const checkoutSessionId = useMemo(() => {
    const s = params.session_id
    const raw = typeof s === 'string' ? s : Array.isArray(s) ? s[0] : undefined
    return typeof raw === 'string' && raw.startsWith('cs_') ? raw : null
  }, [params.session_id])

  const billingHandledRef = useRef(false)
  useEffect(() => {
    if (billingQueryFlag !== 'success' || !user || billingHandledRef.current) return
    billingHandledRef.current = true
    const path = pathname.trim() === '' ? '/packs' : pathname

    const syncAndRefresh = async () => {
      if (checkoutSessionId) {
        const orgAccountId = user?.account?.id ?? null
        const isOrgAdmin = user?.membership?.role === 'ADMIN'
        if (orgAccountId && isOrgAdmin) {
          try {
            await apiClient(API_ENDPOINTS.billing.b2bSyncCheckoutSession(orgAccountId), {
              method: 'POST',
              data: { sessionId: checkoutSessionId },
            })
          } catch {
            // Webhook may have applied; fall through to consumer sync + refresh.
          }
        }
        try {
          await apiClient(API_ENDPOINTS.billing.consumerProSyncCheckoutSession, {
            method: 'POST',
            data: { sessionId: checkoutSessionId },
          })
        } catch {
          // If sync fails (e.g. already synced via webhook), still refresh so UI updates
        }
      } else {
        // Portal return (e.g. subscription_update_confirm) — no session_id, so pull latest
        // subscription state directly from Stripe to avoid showing stale plan if the
        // customer.subscription.updated webhook hasn't fired yet.
        try {
          await apiClient(API_ENDPOINTS.billing.consumerProSyncSubscription, { method: 'POST' })
        } catch {
          // Non-fatal — webhook will eventually update DB
        }
      }
      await refreshAccountAdmin()
      router.replace(path as Href)
    }

    void syncAndRefresh()
  }, [billingQueryFlag, checkoutSessionId, user, refreshAccountAdmin, router, pathname, apiClient])

  const accountId = user?.account?.id ?? null
  const account = user?.account ?? undefined
  const orgPlanTier = account?.planTier ?? null
  const isB2bOrgAdmin = user?.membership?.role === 'ADMIN'
  const stripeNegotiatedPriceId = account?.stripeNegotiatedPriceId ?? null
  const stripeNegotiatedPriceDisplay = account?.stripeNegotiatedPriceDisplay ?? null
  const orgB2bStripeSubscriptionPaid = useMemo(
    () => isOrgB2bStripeSubscriptionStatusPaid(account?.stripeSubscriptionStatus),
    [account?.stripeSubscriptionStatus],
  )
  const orgMaxSeats = account?.maxSeats ?? null

  const { showDialog } = useDialog()
  const { planCards, copy, seatBuckets } = usePricingI18n()

  const { startCheckout, busy: busyB2b } = useB2bBillingActions(accountId, account)
  const { startConsumerProCheckout, openConsumerProPortal, busy: busyConsumer } =
    useConsumerProBillingActions(user?.consumerBilling)

  const [selectedBucketKey, setSelectedBucketKey] = useState(seatBuckets[0]?.key ?? 'BUSINESS_100')
  const userPickedBucketRef = useRef(false)
  const selectBucketKey = useCallback((key: string) => {
    userPickedBucketRef.current = true
    setSelectedBucketKey(key)
  }, [])
  // Match landing: default picker to the org's current Business bucket once account loads.
  useEffect(() => {
    if (userPickedBucketRef.current) return
    if (orgPlanTier !== 'BUSINESS' || typeof orgMaxSeats !== 'number') return
    const current = seatBuckets.find((b) => b.maxSeats === orgMaxSeats)
    if (current && current.key !== selectedBucketKey) {
      setSelectedBucketKey(current.key)
    }
  }, [orgPlanTier, orgMaxSeats, selectedBucketKey, seatBuckets])
  const selectedBucketMaxSeats = useMemo(
    () => seatBuckets.find((b) => b.key === selectedBucketKey)?.maxSeats ?? null,
    [selectedBucketKey, seatBuckets],
  )

  const handleConsumerProCheckout = useCallback((billingInterval: ConsumerProBillingInterval) => {
    void startConsumerProCheckout(billingInterval)
  }, [startConsumerProCheckout])

  const handleContactSales = useCallback(() => {
    router.push(ENTERPRISE_CONTACT_PATH as Href)
  }, [router])

  const handleCreateOrg = useCallback(() => {
    const href = (
      user ? buildAccountAdminNavPath(ACCOUNT_ADMIN_NAV.START_HERE) : ACCOUNT_ADMIN_PUBLIC_ROUTE
    ) as Href
    router.push(href)
  }, [router, user])

  const handleSignIn = useCallback(() => {
    const returnPath = pathname.trim() === '' ? '/packs' : pathname
    router.push(buildPublicSignInHref({ returnUrl: returnPath }) as Href)
  }, [router, pathname])

  const handleContinueToApp = useCallback(() => {
    router.push('/app/friends' as Href)
  }, [router])

  const handleExitPacks = useCallback(() => {
    if (router.canGoBack()) {
      router.back()
      return
    }
    router.replace('/' as Href)
  }, [router])

  const handleManageConsumerSubscription = useCallback(() => {
    // Route by the rail that owns the sub: Stripe → portal; RevenueCat store sub → OS store settings
    // (opening the Stripe portal for a store-bought Pro would show an empty portal).
    if (isStripeManagedProvider(user?.consumerBilling?.proProvider ?? null)) {
      void openConsumerProPortal()
      return
    }
    void Linking.openURL(getStoreSubscriptionsUrl(user?.consumerBilling?.proProvider ?? null))
  }, [openConsumerProPortal, user])

  const handleScheduleDowngradeToMonthly = useCallback(async () => {
    const confirmed = await showDialog({
      title: copy.switchToMonthlyDialogTitle,
      message: copy.switchToMonthlyDialogMsg,
      confirmText: copy.switchToMonthlyConfirm,
      cancelText: copy.switchToMonthlyCancel,
    })
    if (!confirmed) return
    void openConsumerProPortal()
  }, [showDialog, openConsumerProPortal, copy])

  const handleB2bCheckout = useCallback(
    (tier: 'BUSINESS' | 'ENTERPRISE') => {
      const bucketKey = tier === 'BUSINESS' ? selectedBucketKey : undefined
      void startCheckout(tier, bucketKey)
    },
    [startCheckout, selectedBucketKey],
  )

  const handleManageB2bBilling = useCallback(() => {
    const href = (
      user ? buildAccountAdminNavPath(ACCOUNT_ADMIN_NAV.PLAN_BILLING) : ACCOUNT_ADMIN_PUBLIC_ROUTE
    ) as Href
    router.push(href)
  }, [router, user])

  return (
    <View style={styles.screenRoot}>
      <View style={styles.topBar}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={copy.goBack}
          onPress={handleExitPacks}
          style={({ pressed, hovered }) => [
            styles.backButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.backButtonPressed,
            hovered && Platform.OS === 'web' && styles.backButtonHoverWeb,
          ]}
        >
          <ArrowLeft size={22} color={theme.primary} strokeWidth={2.5} />
          <Text style={styles.backLabel}>{copy.goBack}</Text>
        </Pressable>
      </View>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        <Text style={styles.pageTitle}>{copy.pageTitle}</Text>
        <Text style={styles.pageSubtitle}>{copy.pageSubtitle}</Text>

        <View style={styles.cardsGrid}>
          <View style={styles.cardsRow1}>
            {planCards.slice(0, 3).map((plan) => (
              <PricingScreenPlanCard
                key={plan.tier}
                plan={resolvePricingPlanForEnv(plan, isProductionMode)}
                copy={copy}
                seatBuckets={seatBuckets}
                orgPlanTier={orgPlanTier}
                orgMaxSeats={orgMaxSeats}
                selectedBucketMaxSeats={selectedBucketMaxSeats}
                consumerPlanTier={consumerPlanTier}
                consumerProBillingInterval={consumerProBillingInterval}
                consumerCancelAtPeriodEnd={consumerCancelAtPeriodEnd}
                consumerPeriodEndIso={consumerPeriodEndIso}
                hasOrg={accountId != null}
                isB2bOrgAdmin={isB2bOrgAdmin}
                stripeNegotiatedPriceId={stripeNegotiatedPriceId}
                stripeNegotiatedPriceDisplay={stripeNegotiatedPriceDisplay}
                isSignedIn={Boolean(isLoaded && isSignedIn)}
                accountAdminLoading={accountAdminLoading}
                busyB2b={busyB2b}
                busyConsumer={busyConsumer}
                orgB2bStripeSubscriptionPaid={orgB2bStripeSubscriptionPaid}
                theme={theme}
                styles={styles}
                selectedBucketKey={selectedBucketKey}
                onSelectBucketKey={selectBucketKey}
                onB2bCheckout={handleB2bCheckout}
                onManageB2bBilling={handleManageB2bBilling}
                onConsumerProCheckout={handleConsumerProCheckout}
                onContactSales={handleContactSales}
                onCreateOrg={handleCreateOrg}
                onSignIn={handleSignIn}
                onContinueToApp={handleContinueToApp}
                onManageConsumerSubscription={handleManageConsumerSubscription}
                onScheduleDowngradeToMonthly={handleScheduleDowngradeToMonthly}
              />
            ))}
          </View>
          <View style={styles.cardsRow2}>
            {planCards.slice(3).map((plan) => (
              <PricingScreenPlanCard
                key={plan.tier}
                plan={resolvePricingPlanForEnv(plan, isProductionMode)}
                copy={copy}
                seatBuckets={seatBuckets}
                orgPlanTier={orgPlanTier}
                orgMaxSeats={orgMaxSeats}
                selectedBucketMaxSeats={selectedBucketMaxSeats}
                consumerPlanTier={consumerPlanTier}
                consumerProBillingInterval={consumerProBillingInterval}
                consumerCancelAtPeriodEnd={consumerCancelAtPeriodEnd}
                consumerPeriodEndIso={consumerPeriodEndIso}
                hasOrg={accountId != null}
                isB2bOrgAdmin={isB2bOrgAdmin}
                stripeNegotiatedPriceId={stripeNegotiatedPriceId}
                stripeNegotiatedPriceDisplay={stripeNegotiatedPriceDisplay}
                isSignedIn={Boolean(isLoaded && isSignedIn)}
                accountAdminLoading={accountAdminLoading}
                busyB2b={busyB2b}
                busyConsumer={busyConsumer}
                orgB2bStripeSubscriptionPaid={orgB2bStripeSubscriptionPaid}
                theme={theme}
                styles={styles}
                selectedBucketKey={selectedBucketKey}
                onSelectBucketKey={selectBucketKey}
                onB2bCheckout={handleB2bCheckout}
                onManageB2bBilling={handleManageB2bBilling}
                onConsumerProCheckout={handleConsumerProCheckout}
                onContactSales={handleContactSales}
                onCreateOrg={handleCreateOrg}
                onSignIn={handleSignIn}
                onContinueToApp={handleContinueToApp}
                onManageConsumerSubscription={handleManageConsumerSubscription}
                onScheduleDowngradeToMonthly={handleScheduleDowngradeToMonthly}
              />
            ))}
          </View>
        </View>

        <Text style={styles.footnote}>{copy.footnote}</Text>
        <Text style={styles.footnoteSecondary}>{copy.footnoteCancelPolicy}</Text>
        <MarketingPageFooter contactMode="route" />
      </ScrollView>
    </View>
  )
}
